import { useState, useEffect } from "react";

interface BackToTopProps {
  /** The scrollable container element selector. Defaults to window scroll */
  scrollContainerId?: string;
  /** Show after scrolling this many px. Default 300 */
  threshold?: number;
}

export default function BackToTop({ scrollContainerId, threshold = 300 }: BackToTopProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = scrollContainerId ? document.getElementById(scrollContainerId) : null;
    const target = el || window;

    const handleScroll = () => {
      const scrollY = el ? el.scrollTop : (window.scrollY || document.documentElement.scrollTop);
      setVisible(scrollY > threshold);
    };

    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  }, [scrollContainerId, threshold]);

  const handleClick = () => {
    const el = scrollContainerId ? document.getElementById(scrollContainerId) : null;
    if (el) {
      el.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (!visible) return null;

  return (
    <button
      onClick={handleClick}
      aria-label="Back to top"
      className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-50 w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center cursor-pointer transition-all hover:bg-amber-600 active:scale-95"
      style={{ boxShadow: "0 4px 16px rgba(16,185,129,0.35)" }}
    >
      <i className="ri-arrow-up-line text-lg" />
    </button>
  );
}
